import React, { useState, useEffect, useRef } from 'react';
import { getBrandGuide, updateBrandGuide, uploadBrandFont, deleteBrandFont, BrandGuide, CustomFont } from '../services/api';
import { GlassButton } from './ui/GlassButton';
import {
  Target, Palette, Type, MessageSquare, Layout,
  CheckCircle, AlertTriangle, FileText, Info,
  ExternalLink, Save, Pencil, X, Upload, Trash2, Sparkles
} from 'lucide-react';
import LogoStudioModal from './BrandManagement/LogoStudioModal';
import { GlassSelect } from './ui/GlassSelect';
import { GOOGLE_FONT_OPTIONS } from '../data/googleFontOptions';
import { ensureGoogleFont } from '../utils/googleFonts';
import './BrandGuidePreview.css';

interface BrandGuidePreviewProps {
  brandGuide: BrandGuide;
  onClose: () => void;
}

// Helper function to convert HEX to RGB
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Returns true if the background color is light (needs dark logo variant).
const isLightBg = (hex: string): boolean => {
  const rgb = hexToRgb(hex);
  if (!rgb) return true;
  // Relative luminance per WCAG
  const lum = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return lum > 0.55;
};

// Pick the best logo variant for a given background color.
const pickLogoForBg = (bg: BrandGuide, bgHex: string): string | undefined => {
  if (isLightBg(bgHex)) {
    return bg.logo_url || bg.logo_monochrome_black_url || bg.logo_dark_bg_url || bg.logo_monochrome_white_url || undefined;
  }
  return bg.logo_dark_bg_url || bg.logo_monochrome_white_url || bg.logo_url || bg.logo_monochrome_black_url || undefined;
};

const BrandGuidePreview: React.FC<BrandGuidePreviewProps> = ({
  brandGuide: initialBrandGuide,
  onClose
}) => {
  const [brandGuide, setBrandGuide] = useState<BrandGuide>(initialBrandGuide);
  const [activeSection, setActiveSection] = useState<string>('');
  
  // Edit Mode State
  const [isEditing, setIsEditing] = useState(false);
  const [editedGuide, setEditedGuide] = useState<Partial<BrandGuide>>(initialBrandGuide);
  const [isSaving, setIsSaving] = useState(false);

  // Logo Studio modal state
  const [showLogoStudio, setShowLogoStudio] = useState(false);

  // Font upload state
  const [fontUploading, setFontUploading] = useState<'primary' | 'secondary' | null>(null);
  const primaryFontRef = useRef<HTMLInputElement>(null);
  const secondaryFontRef = useRef<HTMLInputElement>(null);

  const handleFontUpload = async (role: 'primary' | 'secondary', file: File) => {
    setFontUploading(role);
    try {
      await uploadBrandFont(brandGuide.brand_guide_id, role, file);
      const freshData = await getBrandGuide(brandGuide.brand_guide_id);
      setBrandGuide(freshData);
      setEditedGuide(freshData);
    } catch (err) {
      alert('Failed to upload font file');
    } finally {
      setFontUploading(null);
    }
  };

  const handleFontDelete = async (filename: string) => {
    if (!window.confirm('Remove this font file?')) return;
    try {
      await deleteBrandFont(brandGuide.brand_guide_id, filename);
      const freshData = await getBrandGuide(brandGuide.brand_guide_id);
      setBrandGuide(freshData);
      setEditedGuide(freshData);
    } catch (err) {
      alert('Failed to remove font');
    }
  };

  // Fetch fresh data on mount to reflect any changes from Brand Portal
  useEffect(() => {
    const fetchFreshData = async () => {
      try {
        const freshData = await getBrandGuide(initialBrandGuide.brand_guide_id);
        setBrandGuide(freshData);
        setEditedGuide(freshData);
      } catch (error) {
        console.error("Failed to refresh brand guide data", error);
      }
    };
    
    fetchFreshData();
  }, [initialBrandGuide.brand_guide_id]);

  // Load custom font files via the Font Loading API whenever brandGuide changes
  useEffect(() => {
    if (!brandGuide.custom_fonts?.length) return;
    brandGuide.custom_fonts.forEach(f => {
      const familyName = f.role === 'primary' ? (brandGuide.primary_font || f.name)
                       : f.role === 'secondary' ? (brandGuide.secondary_font || f.name)
                       : f.name;
      const font = new FontFace(familyName, `url(${f.url})`, { weight: '400', style: 'normal' });
      font.load().then(loaded => document.fonts.add(loaded)).catch(() => {});
    });
  }, [brandGuide.custom_fonts, brandGuide.primary_font, brandGuide.secondary_font]);

  // Live-load named brand fonts from Google Fonts so the specimen renders the
  // real typeface. Tracks edited values for live preview; skips roles backed by
  // a custom uploaded font (loaded via FontFace above).
  useEffect(() => {
    const roles = new Set((brandGuide.custom_fonts || []).map(f => f.role));
    ensureGoogleFont(
      roles.has('primary') ? undefined : editedGuide.primary_font,
      roles.has('secondary') ? undefined : editedGuide.secondary_font,
    );
  }, [editedGuide.primary_font, editedGuide.secondary_font, brandGuide.custom_fonts]);

  const handleEditToggle = () => {
    if (isEditing) {
      setEditedGuide(brandGuide);
    }
    setIsEditing(!isEditing);
  };

  const handleChange = (field: keyof BrandGuide, value: any) => {
    setEditedGuide(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updated = await updateBrandGuide(brandGuide.brand_guide_id, editedGuide);
      setBrandGuide(updated);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save brand guide", error);
      alert("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  // Build table of contents based on available sections
  const sections: { id: string; title: string; page: number }[] = [];
  let pageNumber = 1;

  if (editedGuide.brand_name || editedGuide.tagline || editedGuide.mission_statement || editedGuide.brand_story || isEditing) {
    sections.push({ id: 'brand-identity', title: 'Brand Identity', page: pageNumber++ });
  }
  if (brandGuide.primary_color || brandGuide.secondary_color || brandGuide.accent_color || brandGuide.neutral_colors) {
    sections.push({ id: 'color-palette', title: 'Color Palette', page: pageNumber++ });
  }
  if (brandGuide.primary_font || brandGuide.secondary_font) {
    sections.push({ id: 'typography', title: 'Typography', page: pageNumber++ });
  }
  if (brandGuide.brand_voice || brandGuide.tone_attributes || brandGuide.writing_guidelines || brandGuide.messaging_pillars) {
    sections.push({ id: 'voice-tone', title: 'Voice & Tone', page: pageNumber++ });
  }
  if (brandGuide.design_style || brandGuide.visual_principles || brandGuide.image_style || brandGuide.imagery_guidelines) {
    sections.push({ id: 'design-visual', title: 'Design & Visual Guidelines', page: pageNumber++ });
  }
  if (brandGuide.logo_guidelines || brandGuide.do_not_examples || (brandGuide.primary_color && brandGuide.secondary_color)) {
    sections.push({ id: 'best-practices', title: 'Logo & Best Practices', page: pageNumber++ });
  }
  if (brandGuide.example_headlines || brandGuide.example_ctas) {
    sections.push({ id: 'usage-examples', title: 'Usage Examples', page: pageNumber++ });
  }
  sections.push({ id: 'source-info', title: 'Source Information', page: pageNumber++ });

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const sections = target.querySelectorAll('.preview-section');

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top >= 0 && rect.top <= 200) {
          setActiveSection(section.id);
        }
      });
    };

    const content = document.querySelector('.preview-content');
    content?.addEventListener('scroll', handleScroll);

    return () => {
      content?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="brand-guide-preview-overlay" onClick={onClose}>
      <div className="brand-guide-preview-modal professional-layout" onClick={(e) => e.stopPropagation()}>
        {/* Table of Contents Sidebar */}
        <div className="table-of-contents">
          <div className="toc-header">
            <h3>Contents</h3>
          </div>
          <nav className="toc-nav">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`toc-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                <span className="toc-page">{section.page}</span>
                <span className="toc-title">{section.title}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="preview-main-content">
          <div className="preview-header">
            <div className="header-content">
              <div className="header-title">
                {isEditing ? (
                  <input 
                    className="edit-guide-name"
                    value={editedGuide.brand_guide_name}
                    onChange={(e) => handleChange('brand_guide_name', e.target.value)}
                  />
                ) : (
                  <h1>{brandGuide.brand_guide_name}</h1>
                )}
                <p className="header-subtitle">Brand Identity Guidelines</p>
              </div>
              <div className="header-badges">
                {brandGuide.is_default && <span className="badge-default">DEFAULT</span>}
                <span className={`badge-status ${brandGuide.status}`}>{brandGuide.status}</span>
              </div>
            </div>
            
            <div className="header-actions">
              {isEditing ? (
                <>
                  <GlassButton variant="primary" size="sm" onClick={handleSave} disabled={isSaving} icon={<Save size={15} />}>
                    {isSaving ? 'Saving...' : 'Save'}
                  </GlassButton>
                  <GlassButton variant="ghost" size="sm" onClick={handleEditToggle} icon={<X size={15} />} />
                </>
              ) : (
                <GlassButton variant="secondary" size="sm" onClick={handleEditToggle} icon={<Pencil size={15} />}>
                  Edit
                </GlassButton>
              )}
              <GlassButton variant="ghost" size="sm" onClick={onClose} icon={<X size={18} />} />
            </div>
          </div>

          <div className="preview-content">
          {/* Brand Identity */}
          {(editedGuide.brand_name || editedGuide.tagline || editedGuide.mission_statement || editedGuide.brand_story || isEditing) && (
            <section id="brand-identity" className="preview-section page-section">
              <div className="section-header">
                <span className="page-number">{sections.find(s => s.id === 'brand-identity')?.page}</span>
                <h2><Target size={20} /> Brand Identity</h2>
              </div>

              <div className="field">
                <label>Brand Name</label>
                {isEditing ? (
                  <input 
                    className="edit-input"
                    value={editedGuide.brand_name || ''}
                    onChange={(e) => handleChange('brand_name', e.target.value)}
                  />
                ) : (
                  <p className="brand-name">{brandGuide.brand_name}</p>
                )}
              </div>

              <div className="field">
                <label>Tagline</label>
                {isEditing ? (
                  <input 
                    className="edit-input"
                    value={editedGuide.tagline || ''}
                    onChange={(e) => handleChange('tagline', e.target.value)}
                  />
                ) : (
                  <p className="tagline">"{brandGuide.tagline}"</p>
                )}
              </div>

              <div className="field">
                <label>Mission Statement</label>
                {isEditing ? (
                  <textarea 
                    className="edit-textarea"
                    value={editedGuide.mission_statement || ''}
                    onChange={(e) => handleChange('mission_statement', e.target.value)}
                    rows={3}
                  />
                ) : (
                  <p>{brandGuide.mission_statement}</p>
                )}
              </div>

              <div className="field">
                <label>Brand Story</label>
                {isEditing ? (
                  <textarea 
                    className="edit-textarea"
                    value={editedGuide.brand_story || ''}
                    onChange={(e) => handleChange('brand_story', e.target.value)}
                    rows={5}
                  />
                ) : (
                  <p>{brandGuide.brand_story}</p>
                )}
              </div>
            </section>
          )}

          {/* Colors - Enhanced Professional Display */}
          {(editedGuide.primary_color || editedGuide.secondary_color || editedGuide.accent_color || editedGuide.neutral_colors || isEditing) && (
            <section id="color-palette" className="preview-section page-section color-section">
              <div className="section-header">
                <span className="page-number">{sections.find(s => s.id === 'color-palette')?.page}</span>
                <h2><Palette size={20} /> Color Palette</h2>
              </div>

              <div className="professional-color-grid">
                {/* Primary Colors */}
                {(editedGuide.primary_color || isEditing) && (() => {
                  const colorValue = editedGuide.primary_color || 'var(--text)';
                  const rgb = hexToRgb(colorValue);
                  return (
                    <div className="color-spec-card">
                      <div className="color-swatch-large" style={{ backgroundColor: colorValue }}>
                        {isEditing && (
                          <input 
                            type="color" 
                            className="color-picker-overlay"
                            value={colorValue}
                            onChange={(e) => handleChange('primary_color', e.target.value)}
                          />
                        )}
                      </div>
                      <div className="color-details">
                        <h4>Primary Color</h4>
                        <div className="color-values">
                          <div className="color-value">
                            <span className="value-label">HEX</span>
                            {isEditing ? (
                              <input 
                                className="edit-hex-input"
                                value={colorValue.toUpperCase()}
                                onChange={(e) => handleChange('primary_color', e.target.value)}
                              />
                            ) : (
                              <span className="value-code">{colorValue.toUpperCase()}</span>
                            )}
                          </div>
                          {rgb && (
                            <div className="color-value">
                              <span className="value-label">RGB</span>
                              <span className="value-code">{rgb.r} {rgb.g} {rgb.b}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {(editedGuide.secondary_color || isEditing) && (() => {
                  const colorValue = editedGuide.secondary_color || 'var(--panel-strong)';
                  const rgb = hexToRgb(colorValue);
                  return (
                    <div className="color-spec-card">
                      <div className="color-swatch-large" style={{ backgroundColor: colorValue }}>
                        {isEditing && (
                          <input 
                            type="color" 
                            className="color-picker-overlay"
                            value={colorValue}
                            onChange={(e) => handleChange('secondary_color', e.target.value)}
                          />
                        )}
                      </div>
                      <div className="color-details">
                        <h4>Secondary Color</h4>
                        <div className="color-values">
                          <div className="color-value">
                            <span className="value-label">HEX</span>
                            {isEditing ? (
                              <input 
                                className="edit-hex-input"
                                value={colorValue.toUpperCase()}
                                onChange={(e) => handleChange('secondary_color', e.target.value)}
                              />
                            ) : (
                              <span className="value-code">{colorValue.toUpperCase()}</span>
                            )}
                          </div>
                          {rgb && (
                            <div className="color-value">
                              <span className="value-label">RGB</span>
                              <span className="value-code">{rgb.r} {rgb.g} {rgb.b}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {(editedGuide.accent_color || isEditing) && (() => {
                  const colorValue = editedGuide.accent_color || '#3498db';
                  const rgb = hexToRgb(colorValue);
                  return (
                    <div className="color-spec-card">
                      <div className="color-swatch-large" style={{ backgroundColor: colorValue }}>
                        {isEditing && (
                          <input 
                            type="color" 
                            className="color-picker-overlay"
                            value={colorValue}
                            onChange={(e) => handleChange('accent_color', e.target.value)}
                          />
                        )}
                      </div>
                      <div className="color-details">
                        <h4>Accent Color</h4>
                        <div className="color-values">
                          <div className="color-value">
                            <span className="value-label">HEX</span>
                            {isEditing ? (
                              <input 
                                className="edit-hex-input"
                                value={colorValue.toUpperCase()}
                                onChange={(e) => handleChange('accent_color', e.target.value)}
                              />
                            ) : (
                              <span className="value-code">{colorValue.toUpperCase()}</span>
                            )}
                          </div>
                          {rgb && (
                            <div className="color-value">
                              <span className="value-label">RGB</span>
                              <span className="value-code">{rgb.r} {rgb.g} {rgb.b}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Neutral Colors */}
              {(editedGuide.neutral_colors || isEditing) && (
                <div className="neutral-colors-section">
                  <h4>Neutral Colors</h4>
                  <div className="neutral-color-grid">
                    {(editedGuide.neutral_colors || ['#F8F9FA', '#E9ECEF', '#495057']).map((color, idx) => {
                      return (
                        <div key={idx} className="neutral-color-card">
                          <div className="neutral-swatch" style={{ backgroundColor: color }}>
                            {isEditing && (
                              <input
                                type="color"
                                className="color-picker-overlay-small"
                                value={color}
                                onChange={(e) => {
                                  const newNeutrals = [...(editedGuide.neutral_colors || ['#F8F9FA', '#E9ECEF', '#495057'])];
                                  newNeutrals[idx] = e.target.value;
                                  handleChange('neutral_colors', newNeutrals);
                                }}
                              />
                            )}
                          </div>
                          <div className="neutral-details">
                            <div className="color-value-compact">
                              <span className="value-label">HEX</span>
                              {isEditing ? (
                                <input
                                  className="edit-hex-input-small"
                                  value={color.toUpperCase()}
                                  onChange={(e) => {
                                    const newNeutrals = [...(editedGuide.neutral_colors || ['#F8F9FA', '#E9ECEF', '#495057'])];
                                    newNeutrals[idx] = e.target.value;
                                    handleChange('neutral_colors', newNeutrals);
                                  }}
                                />
                              ) : (
                                <span className="value-code">{color.toUpperCase()}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Typography - Enhanced Professional Display */}
          {(editedGuide.primary_font || editedGuide.secondary_font || isEditing) && (
            <section id="typography" className="preview-section page-section typography-section">
              <div className="section-header">
                <span className="page-number">{sections.find(s => s.id === 'typography')?.page}</span>
                <h2><Type size={20} /> Typography</h2>
              </div>

              {(editedGuide.primary_font || isEditing) && (
                <div className="font-specimen">
                  <div className="font-header">
                    {isEditing ? (
                      <div className="edit-font-container">
                        <label>Primary Font Name</label>
                        <GlassSelect
                          options={GOOGLE_FONT_OPTIONS}
                          value={editedGuide.primary_font || ''}
                          onChange={(v) => handleChange('primary_font', v)}
                          searchable
                          allowCustom
                          size="sm"
                          placeholder="Search Google Fonts…"
                        />
                      </div>
                    ) : (
                      <h4>{brandGuide.primary_font}</h4>
                    )}
                    <span className="font-usage-badge">Primary Font - Headings</span>
                  </div>

                  <div className="font-sample" style={{ fontFamily: editedGuide.primary_font || 'inherit' }}>
                    <div className="alphabet-display">
                      <div className="alphabet-line uppercase">ABCDEFGHIJKLMNOPQRSTUVWXYZ</div>
                      <div className="alphabet-line lowercase">abcdefghijklmnopqrstuvwxyz</div>
                      <div className="alphabet-line numbers">1234567890!@#$%^&*()</div>
                    </div>
                  </div>

                  <div className="font-meta">
                    <div className="when-to-use">
                      <strong>When to Use:</strong>
                      <p>Use for all headings, titles, and prominent text elements. Ideal for capturing attention and establishing hierarchy.</p>
                    </div>
                    {brandGuide.primary_font_url && !brandGuide.primary_font_url.match(/\.(woff2?|ttf|otf)$/) && (
                      <div className="font-link">
                        <a href={brandGuide.primary_font_url} target="_blank" rel="noopener noreferrer">
                          View Font Family →
                        </a>
                      </div>
                    )}
                    {/* Custom font files */}
                    {(brandGuide.custom_fonts || []).filter(f => f.role === 'primary').map(f => (
                      <div key={f.filename} className="font-file-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{f.filename}</span>
                        <span style={{ color: 'var(--success)', fontSize: '11px' }}>custom font loaded</span>
                        {isEditing && (
                          <button onClick={() => handleFontDelete(f.filename)} title="Remove font" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '2px' }}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    {isEditing && (
                      <div style={{ marginTop: '10px' }}>
                        <input
                          ref={primaryFontRef}
                          type="file"
                          accept=".woff2,.woff,.ttf,.otf"
                          style={{ display: 'none' }}
                          onChange={(e) => { if (e.target.files?.[0]) handleFontUpload('primary', e.target.files[0]); }}
                        />
                        <GlassButton variant="secondary" size="sm" onClick={() => primaryFontRef.current?.click()} icon={<Upload size={14} />} disabled={fontUploading === 'primary'}>
                          {fontUploading === 'primary' ? 'Uploading…' : 'Upload Font File'}
                        </GlassButton>
                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>Accepted: .woff2, .woff, .ttf, .otf (max 5 MB)</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(editedGuide.secondary_font || isEditing) && (
                <div className="font-specimen">
                  <div className="font-header">
                    {isEditing ? (
                      <div className="edit-font-container">
                        <label>Secondary Font Name</label>
                        <GlassSelect
                          options={GOOGLE_FONT_OPTIONS}
                          value={editedGuide.secondary_font || ''}
                          onChange={(v) => handleChange('secondary_font', v)}
                          searchable
                          allowCustom
                          size="sm"
                          placeholder="Search Google Fonts…"
                        />
                      </div>
                    ) : (
                      <h4>{brandGuide.secondary_font}</h4>
                    )}
                    <span className="font-usage-badge secondary">Secondary Font - Body Text</span>
                  </div>

                  <div className="font-sample" style={{ fontFamily: editedGuide.secondary_font || 'inherit' }}>
                    <div className="alphabet-display">
                      <div className="alphabet-line uppercase">ABCDEFGHIJKLMNOPQRSTUVWXYZ</div>
                      <div className="alphabet-line lowercase">abcdefghijklmnopqrstuvwxyz</div>
                      <div className="alphabet-line numbers">1234567890!@#$%^&*()</div>
                    </div>
                  </div>

                  <div className="font-meta">
                    <div className="when-to-use">
                      <strong>When to Use:</strong>
                      <p>Use for body text, descriptions, and supporting content. Optimized for readability across all platforms.</p>
                    </div>
                    {brandGuide.secondary_font_url && !brandGuide.secondary_font_url.match(/\.(woff2?|ttf|otf)$/) && (
                      <div className="font-link">
                        <a href={brandGuide.secondary_font_url} target="_blank" rel="noopener noreferrer">
                          View Font Family →
                        </a>
                      </div>
                    )}
                    {/* Custom font files */}
                    {(brandGuide.custom_fonts || []).filter(f => f.role === 'secondary').map(f => (
                      <div key={f.filename} className="font-file-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{f.filename}</span>
                        <span style={{ color: 'var(--success)', fontSize: '11px' }}>custom font loaded</span>
                        {isEditing && (
                          <button onClick={() => handleFontDelete(f.filename)} title="Remove font" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '2px' }}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    {isEditing && (
                      <div style={{ marginTop: '10px' }}>
                        <input
                          ref={secondaryFontRef}
                          type="file"
                          accept=".woff2,.woff,.ttf,.otf"
                          style={{ display: 'none' }}
                          onChange={(e) => { if (e.target.files?.[0]) handleFontUpload('secondary', e.target.files[0]); }}
                        />
                        <GlassButton variant="secondary" size="sm" onClick={() => secondaryFontRef.current?.click()} icon={<Upload size={14} />} disabled={fontUploading === 'secondary'}>
                          {fontUploading === 'secondary' ? 'Uploading…' : 'Upload Font File'}
                        </GlassButton>
                        <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>Accepted: .woff2, .woff, .ttf, .otf (max 5 MB)</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Voice & Tone */}
          {(brandGuide.brand_voice || brandGuide.tone_attributes || brandGuide.writing_guidelines || brandGuide.messaging_pillars) && (
            <section id="voice-tone" className="preview-section page-section">
              <div className="section-header">
                <span className="page-number">{sections.find(s => s.id === 'voice-tone')?.page}</span>
                <h2><MessageSquare size={20} /> Voice & Tone</h2>
              </div>

              {brandGuide.brand_voice && (
                <div className="field">
                  <label>Brand Voice</label>
                  <p className="highlight">{brandGuide.brand_voice}</p>
                </div>
              )}

              {brandGuide.tone_attributes && brandGuide.tone_attributes.length > 0 && (
                <div className="field">
                  <label>Tone Attributes</label>
                  <div className="tags">
                    {brandGuide.tone_attributes.map((attr, idx) => (
                      <span key={idx} className="tag">{attr}</span>
                    ))}
                  </div>
                </div>
              )}

              {brandGuide.writing_guidelines && (
                <div className="field">
                  <label>Writing Guidelines</label>
                  <p>{brandGuide.writing_guidelines}</p>
                </div>
              )}

              {brandGuide.messaging_pillars && brandGuide.messaging_pillars.length > 0 && (
                <div className="field">
                  <label>Messaging Pillars</label>
                  <ul className="pillars-list">
                    {brandGuide.messaging_pillars.map((pillar, idx) => (
                      <li key={idx}>{pillar}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {/* Design & Visual */}
          {(brandGuide.design_style || brandGuide.visual_principles || brandGuide.image_style || brandGuide.imagery_guidelines || brandGuide.logo_guidelines) && (
            <section id="design-visual" className="preview-section page-section">
              <div className="section-header">
                <span className="page-number">{sections.find(s => s.id === 'design-visual')?.page}</span>
                <h2><Layout size={20} /> Design & Visual Guidelines</h2>
              </div>

              {brandGuide.design_style && (
                <div className="field">
                  <label>Design Style</label>
                  <p className="highlight">{brandGuide.design_style}</p>
                </div>
              )}

              {brandGuide.visual_principles && brandGuide.visual_principles.length > 0 && (
                <div className="field">
                  <label>Visual Principles</label>
                  <ul className="pillars-list">
                    {brandGuide.visual_principles.map((principle, idx) => (
                      <li key={idx}>{principle}</li>
                    ))}
                  </ul>
                </div>
              )}

              {brandGuide.ui_preferences && (
                <div className="field">
                  <label>UI Preferences</label>
                  <div className="ui-preferences-visual">
                    {typeof brandGuide.ui_preferences === 'object' && (
                      <>
                        {brandGuide.ui_preferences.buttons && (
                          <div className="ui-preference-item">
                            <div className="ui-preference-label">Button Style</div>
                            <div className="ui-preference-demo">
                              <button
                                className={`demo-button ${brandGuide.ui_preferences.buttons.style} ${brandGuide.ui_preferences.buttons.effect}`}
                                style={{
                                  backgroundColor: brandGuide.primary_color || '#3498db',
                                  color: 'var(--text-inverse)'
                                }}
                              >
                                Sample Button
                              </button>
                              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', alignSelf: 'center' }}>
                                Style: {brandGuide.ui_preferences.buttons.style} •
                                Size: {brandGuide.ui_preferences.buttons.size} •
                                Effect: {brandGuide.ui_preferences.buttons.effect}
                              </div>
                            </div>
                          </div>
                        )}

                        {brandGuide.ui_preferences.spacing && (
                          <div className="ui-preference-item">
                            <div className="ui-preference-label">Spacing</div>
                            <p style={{ fontSize: '15px', color: 'var(--text-secondary)', margin: 0 }}>
                              {brandGuide.ui_preferences.spacing.charAt(0).toUpperCase() + brandGuide.ui_preferences.spacing.slice(1)}
                            </p>
                          </div>
                        )}

                        {brandGuide.ui_preferences.layout && (
                          <div className="ui-preference-item">
                            <div className="ui-preference-label">Layout</div>
                            <p style={{ fontSize: '15px', color: 'var(--text-secondary)', margin: 0 }}>
                              {brandGuide.ui_preferences.layout.split('-').map((word: string) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                              ).join(' ')}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {brandGuide.image_style && (
                <div className="field">
                  <label>Image Style</label>
                  <p>{brandGuide.image_style}</p>
                </div>
              )}

              {brandGuide.imagery_guidelines && (
                <div className="field">
                  <label>Imagery Guidelines</label>
                  <p>{brandGuide.imagery_guidelines}</p>
                </div>
              )}

              <div className="field">
                <label>Logo</label>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                  {brandGuide.logo_url ? (
                    <img src={brandGuide.logo_url} alt="Brand Logo" className="logo-preview" />
                  ) : (
                    <div style={{
                      padding: '16px 20px',
                      borderRadius: '8px',
                      border: '1px dashed var(--stroke)',
                      color: 'var(--text-secondary)',
                      fontSize: '13px',
                    }}>
                      No logo yet
                    </div>
                  )}
                  {isEditing && (
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      icon={<Sparkles size={15} />}
                      onClick={() => setShowLogoStudio(true)}
                    >
                      {brandGuide.logo_url ? 'Regenerate Logo' : 'Generate Logo'}
                    </GlassButton>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Logo & Best Practices */}
          {(brandGuide.logo_guidelines || brandGuide.do_not_examples || (brandGuide.primary_color && brandGuide.secondary_color)) && (
            <section id="best-practices" className="preview-section page-section best-practices-section">
              <div className="section-header">
                <span className="page-number">{sections.find(s => s.id === 'best-practices')?.page}</span>
                <h2><CheckCircle size={20} /> Logo & Best Practices</h2>
              </div>

              {/* Logo Guidelines */}
              {brandGuide.logo_guidelines && (
                <div className="logo-guidelines-container">
                  <h3 className="subsection-title">Logo Guidelines</h3>
                  <div className="logo-guidelines-box">
                    <div className="guidelines-icon"><Target size={40} /></div>
                    <div className="guidelines-content">
                      {brandGuide.logo_guidelines.split('\n').map((line, idx) => (
                        <div key={idx} className="guideline-item">
                          {line.trim().startsWith('•') ? line.trim() : `• ${line.trim()}`}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Background Usage Examples */}
              {brandGuide.primary_color && brandGuide.secondary_color && (
                <div className="background-usage-container">
                  <h3 className="subsection-title">Background Usage</h3>
                  <p className="subsection-description">
                    Logo and text should maintain proper contrast and visibility across different backgrounds.
                  </p>

                  {(() => {
                    // Use any mono variant as the alpha mask (mono_white preferred — has transparent bg + opaque ink).
                    const maskLogo =
                      brandGuide.logo_monochrome_white_url ||
                      brandGuide.logo_monochrome_black_url ||
                      brandGuide.logo_dark_bg_url ||
                      brandGuide.logo_url;
                    const maskStyle = (color: string): React.CSSProperties => ({
                      WebkitMaskImage: `url(${maskLogo})`,
                      maskImage: `url(${maskLogo})`,
                      WebkitMaskRepeat: 'no-repeat',
                      maskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      maskPosition: 'center',
                      WebkitMaskSize: 'contain',
                      maskSize: 'contain',
                      backgroundColor: color,
                    });
                    return (
                      <div className="background-grid">
                        {/* Primary colour: primary-coloured logo on secondary-coloured bg */}
                        {brandGuide.secondary_color && (
                          <div className="background-example" style={{ backgroundColor: brandGuide.secondary_color }}>
                            <div className="background-label">Primary Colour</div>
                            {maskLogo ? (
                              <div className="background-logo" style={maskStyle(brandGuide.primary_color!)} aria-label="Primary-colour logo on secondary background" />
                            ) : brandGuide.brand_name && (
                              <div className="background-text" style={{ color: brandGuide.primary_color }}>
                                {brandGuide.brand_name}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Secondary colour: secondary-coloured logo on primary-coloured bg */}
                        <div className="background-example" style={{ backgroundColor: brandGuide.primary_color }}>
                          <div className="background-label">Secondary Colour</div>
                          {maskLogo && brandGuide.secondary_color ? (
                            <div className="background-logo" style={maskStyle(brandGuide.secondary_color)} aria-label="Secondary-colour logo on primary background" />
                          ) : brandGuide.brand_name && (
                            <div className="background-text" style={{ color: brandGuide.secondary_color || 'var(--text-inverse)' }}>
                              {brandGuide.brand_name}
                            </div>
                          )}
                        </div>

                        {/* White background: black logo */}
                        <div className="background-example" style={{ backgroundColor: '#ffffff', border: '2px solid var(--panel-border)' }}>
                          <div className="background-label" style={{ color: '#333' }}>White Background</div>
                          {brandGuide.logo_monochrome_black_url ? (
                            <img src={brandGuide.logo_monochrome_black_url} alt="Black logo on white" className="background-logo" />
                          ) : maskLogo ? (
                            <div className="background-logo" style={maskStyle('#000000')} aria-label="Black logo on white background" />
                          ) : brandGuide.brand_name && (
                            <div className="background-text" style={{ color: '#000' }}>
                              {brandGuide.brand_name}
                            </div>
                          )}
                        </div>

                        {/* Dark background */}
                        {(() => {
                          const logo = pickLogoForBg(brandGuide, '#111111');
                          return (
                            <div className="background-example" style={{ backgroundColor: '#111111' }}>
                              <div className="background-label">Dark Background</div>
                              {logo ? (
                                <img src={logo} alt="Logo on dark" className="background-logo" />
                              ) : brandGuide.brand_name && (
                                <div className="background-text" style={{ color: 'var(--text-inverse)' }}>
                                  {brandGuide.brand_name}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Do's and Don'ts */}
              {brandGuide.do_not_examples && brandGuide.do_not_examples.length > 0 && (
                <div className="donot-container">
                  <h3 className="subsection-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><AlertTriangle size={16} /> Do Not Examples</h3>
                  <div className="donot-grid">
                    {brandGuide.do_not_examples.map((example, idx) => (
                      <div key={idx} className="donot-card">
                        <div className="donot-icon">✗</div>
                        <div className="donot-text">{example}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Examples */}
          {(brandGuide.example_headlines || brandGuide.example_ctas) && (
            <section id="usage-examples" className="preview-section page-section">
              <div className="section-header">
                <span className="page-number">{sections.find(s => s.id === 'usage-examples')?.page}</span>
                <h2><FileText size={20} /> Usage Examples</h2>
              </div>

              {brandGuide.example_headlines && brandGuide.example_headlines.length > 0 && (
                <div className="field">
                  <label>Example Headlines</label>
                  <ul className="examples-list">
                    {brandGuide.example_headlines.map((headline, idx) => (
                      <li key={idx} className="example-item">"{headline}"</li>
                    ))}
                  </ul>
                </div>
              )}

              {brandGuide.example_ctas && brandGuide.example_ctas.length > 0 && (
                <div className="field">
                  <label>Example CTAs</label>
                  <div className="cta-examples">
                    {brandGuide.example_ctas.map((cta, idx) => (
                      <span key={idx} className="cta-badge">{cta}</span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Source Information */}
          <section id="source-info" className="preview-section page-section">
            <div className="section-header">
              <span className="page-number">{sections.find(s => s.id === 'source-info')?.page}</span>
              <h2><Info size={20} /> Source Information</h2>
            </div>

            <div className="field">
              <label>Source Type</label>
              <p>{brandGuide.source_type || 'Manual'}</p>
            </div>

            {brandGuide.source_url && (
              <div className="field">
                <label>Source URL</label>
                <p><a href={brandGuide.source_url} target="_blank" rel="noopener noreferrer">{brandGuide.source_url}</a></p>
              </div>
            )}

            {brandGuide.llm_model && (
              <div className="field">
                <label>Generated With</label>
                <p>{brandGuide.llm_model}</p>
              </div>
            )}

            <div className="field">
              <label>Created</label>
              <p>{new Date(brandGuide.created_at).toLocaleString()}</p>
            </div>

            <div className="field">
              <label>Last Updated</label>
              <p>{new Date(brandGuide.updated_at).toLocaleString()}</p>
            </div>
          </section>
        </div>

          <div className="preview-footer">
            <div className="footer-brand">
              <span>{brandGuide.brand_guide_name}</span>
              <span className="footer-separator">•</span>
              <span>Brand Identity Guidelines</span>
            </div>
            <div className="footer-actions" style={{ display: 'flex', gap: '12px' }}>
              <GlassButton
                variant="primary"
                onClick={() => window.open(`/brand-portal/${brandGuide.brand_guide_id}`, '_blank')}
                icon={<ExternalLink size={16} />}
              >
                Launch Brand Portal
              </GlassButton>
              <GlassButton variant="secondary" onClick={onClose}>Close</GlassButton>
            </div>
          </div>
        </div>
      </div>

      {showLogoStudio && (
        <LogoStudioModal
          brandGuideId={brandGuide.brand_guide_id}
          brandName={brandGuide.brand_name || 'Brand'}
          primaryColor={brandGuide.primary_color || '#2177FF'}
          currentLogoUrl={brandGuide.logo_url || undefined}
          onClose={() => setShowLogoStudio(false)}
          onLogoUpdated={async () => {
            try {
              const fresh = await getBrandGuide(brandGuide.brand_guide_id);
              setBrandGuide(fresh);
              setEditedGuide(fresh);
            } catch {
              // ignore — fall back to old data
            }
          }}
        />
      )}
    </div>
  );
};

export default BrandGuidePreview;
